import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import './stylesheets/BugReports.css';

interface BugReport {
  id: string;
  area: string;
  created_at: Timestamp;
  description: string;
  recaptcha: any;
  specialism: string;
  user_email: string;
  completed?: boolean;
}

interface DescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  description: string;
  bugArea: string;
}

const DescriptionModal: React.FC<DescriptionModalProps> = ({ isOpen, onClose, description, bugArea }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bug Description - {bugArea}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>{description || 'No description provided'}</p>
        </div>
        <div className="modal-footer">
          <button className="modal-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const BugReports: React.FC<{ onBugStatusChange?: () => void }> = ({ onBugStatusChange }) => {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bugFilter, setBugFilter] = useState<'all' | 'reportedToday' | 'last30Days' | 'pending' | 'completed'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ 
    key: 'created_at', 
    direction: 'desc' 
  });
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [selectedDescription, setSelectedDescription] = useState<{description: string, area: string} | null>(null);

  useEffect(() => {
    loadBugReports();
  }, []);

  const loadBugReports = async () => {
    setLoading(true);
    try {
      const bugsCollection = collection(db, 'reported_bugs');
      const bugsSnapshot = await getDocs(bugsCollection);
      const bugsList = bugsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BugReport[];
      
      setBugReports(bugsList);
    } catch (error) {
      console.error('Error loading bug reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBugStatus = async (bugId: string, completed: boolean) => {
    setUpdatingStatus(bugId);
    try {
      const bugRef = doc(db, 'reported_bugs', bugId);
      await updateDoc(bugRef, {
        completed: completed
      });
      
      // Update local state
      setBugReports(prev => 
        prev.map(bug => 
          bug.id === bugId 
            ? { ...bug, completed: completed }
            : bug
        )
      );
      
      // Notify parent component to refresh pending bugs indicator
      onBugStatusChange?.();
    } catch (error) {
      console.error('Error updating bug status:', error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Helper function to format date
  const formatDate = (timestamp: Timestamp): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString();
  };

  // Helper function to get area badge class
  const getAreaBadgeClass = (area: string): string => {
    const normalizedArea = area.toLowerCase().replace(/\s+/g, '-');
    switch (normalizedArea) {
      case 'question-bank':
        return 'question-bank';
      case 'interview':
        return 'interview';
      case 'general':
        return 'general';
      default:
        return 'other';
    }
  };

  // Table sorting and filtering
  const filteredAndSortedBugs = useMemo(() => {
    let filtered = [...bugReports];
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Apply analytics card filter
    if (bugFilter === 'reportedToday') {
      filtered = filtered.filter(bug => {
        if (!bug.created_at) return false;
        const bugDate = bug.created_at.toDate();
        return bugDate >= todayStart && bugDate <= now;
      });
    } else if (bugFilter === 'last30Days') {
      filtered = filtered.filter(bug => {
        if (!bug.created_at) return false;
        const bugDate = bug.created_at.toDate();
        return bugDate >= thirtyDaysAgo && bugDate <= now;
      });
    } else if (bugFilter === 'pending') {
      filtered = filtered.filter(bug => !bug.completed);
    } else if (bugFilter === 'completed') {
      filtered = filtered.filter(bug => bug.completed);
    }

    // Apply search filter
    const term = search.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(bug => {
        const searchableFields = [
          bug.area || '',
          bug.description || '',
          bug.specialism || '',
          bug.user_email || ''
        ];
        return searchableFields.some(field => 
          field.toLowerCase().includes(term)
        );
      });
    }

    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      const { key, direction } = sortConfig;
      const dir = direction === 'asc' ? 1 : -1;

      let aVal: any = (a as any)[key];
      let bVal: any = (b as any)[key];

      // Handle timestamp sorting
      if (key === 'created_at') {
        aVal = aVal ? aVal.toDate().getTime() : 0;
        bVal = bVal ? bVal.toDate().getTime() : 0;
      }

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * dir;
    });

    return sorted;
  }, [bugReports, search, bugFilter, sortConfig]);

  const toggleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'created_at' ? 'desc' : 'asc' };
    });
  };

  const sortIndicator = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Calculate analytics
  const calculateAnalytics = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // 1. Total bugs
    const totalBugs = bugReports.length;

    // 2. Bugs reported today
    const bugsToday = bugReports.filter(bug => {
      if (!bug.created_at) return false;
      const bugDate = bug.created_at.toDate();
      return bugDate >= todayStart && bugDate <= now;
    }).length;

    // 3. Bugs in last 30 days
    const bugsLast30Days = bugReports.filter(bug => {
      if (!bug.created_at) return false;
      const bugDate = bug.created_at.toDate();
      return bugDate >= thirtyDaysAgo && bugDate <= now;
    }).length;

    // 4. Completed bugs count
    const completedBugs = bugReports.filter(bug => bug.completed).length;

    return {
      totalBugs,
      bugsToday,
      bugsLast30Days,
      completedBugs
    };
  };

  const analytics = calculateAnalytics();

  const clearFilters = () => {
    setSearch('');
    setBugFilter('all');
  };

  return (
    <div className="bug-reports-page">
      <h2>Bug Reports</h2>
      
      {/* Analytics Cards */}
      <div className="analytics-grid" style={{marginBottom: '30px'}}>
        <div
          className={`analytics-card${bugFilter==='all' ? ' active' : ''}`}
          style={{cursor:'pointer', border: bugFilter==='all' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setBugFilter(bugFilter==='all' ? 'all' : 'all')}
        >
          <h3>Total Bug Reports</h3>
          <div className="analytics-number">{analytics.totalBugs}</div>
        </div>
        <div
          className={`analytics-card${bugFilter==='reportedToday' ? ' active' : ''}`}
          style={{cursor:'pointer', border: bugFilter==='reportedToday' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setBugFilter(bugFilter==='reportedToday' ? 'all' : 'reportedToday')}
        >
          <h3>Reported Today</h3>
          <div className="analytics-number">{analytics.bugsToday}</div>
        </div>
        <div
          className={`analytics-card${bugFilter==='last30Days' ? ' active' : ''}`}
          style={{cursor:'pointer', border: bugFilter==='last30Days' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setBugFilter(bugFilter==='last30Days' ? 'all' : 'last30Days')}
        >
          <h3>Last 30 Days</h3>
          <div className="analytics-number">{analytics.bugsLast30Days}</div>
        </div>
        <div
          className={`analytics-card${bugFilter==='pending' ? ' active' : ''}`}
          style={{cursor:'pointer', border: bugFilter==='pending' ? '2px solid #64b5f6' : undefined}}
          onClick={() => setBugFilter(bugFilter==='pending' ? 'all' : 'pending')}
        >
          <h3>Pending Bugs</h3>
          <div className="analytics-number">{analytics.totalBugs - analytics.completedBugs}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading bug reports...</div>
      ) : (
        <div className="bugs-table-wrapper">
          <div style={{marginBottom: '16px', display:'flex', flexWrap:'wrap', gap:'12px', alignItems:'center'}}>
            <input
              type="text"
              placeholder="Search by area, description, specialism, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 6,
                border: '5px solid #949494ff',
                background: '#585757ff',
                color: '#f3f3f3ff',
                flex: '1 1 260px'
              }}
            />
            
            {search && (
              <button
                onClick={clearFilters}
                style={{
                  background:'#444',
                  color:'#eee',
                  border:'1px solid #555',
                  padding:'10px 16px',
                  borderRadius:6,
                  cursor:'pointer'
                }}
              >
                Clear
              </button>
            )}
            
            <span style={{marginLeft:'auto', color:'#64b5f6', fontWeight:600, fontSize:'16px', paddingRight:'18px'}}>
              {filteredAndSortedBugs.length} result{filteredAndSortedBugs.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="bugs-table-container">
            <table className="bugs-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('area')}>
                    Area{sortIndicator('area')}
                  </th>
                  <th onClick={() => toggleSort('created_at')}>
                    Date Reported{sortIndicator('created_at')}
                  </th>
                  <th onClick={() => toggleSort('user_email')}>
                    User Email{sortIndicator('user_email')}
                  </th>
                  <th onClick={() => toggleSort('specialism')}>
                    Specialism{sortIndicator('specialism')}
                  </th>
                  <th>Description</th>
                  <th onClick={() => toggleSort('completed')}>
                    Status{sortIndicator('completed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedBugs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-data">
                      No bug reports found.
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedBugs.map((bug) => (
                    <tr key={bug.id}>
                      <td data-label="Area">
                        <span className={`area-badge ${getAreaBadgeClass(bug.area || '')}`}>
                          {bug.area || 'Unknown'}
                        </span>
                      </td>
                      <td data-label="Date Reported">
                        {formatDate(bug.created_at)}
                      </td>
                      <td data-label="User Email">
                        {bug.user_email || 'N/A'}
                      </td>
                      <td data-label="Specialism">
                        {bug.specialism || 'N/A'}
                      </td>
                      <td data-label="Description">
                        <div 
                          className="description-preview clickable" 
                          title="Click to view full description"
                          onClick={() => setSelectedDescription({
                            description: bug.description || 'No description provided',
                            area: bug.area || 'Unknown'
                          })}
                        >
                          {bug.description || 'No description provided'}
                        </div>
                      </td>
                      <td data-label="Status">
                        <select
                          className="status-dropdown"
                          value={bug.completed ? 'completed' : 'pending'}
                          onChange={(e) => updateBugStatus(bug.id, e.target.value === 'completed')}
                          disabled={updatingStatus === bug.id}
                        >
                          <option value="pending">⏳ Pending</option>
                          <option value="completed">✅ Completed</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Description Modal */}
      <DescriptionModal
        isOpen={selectedDescription !== null}
        onClose={() => setSelectedDescription(null)}
        description={selectedDescription?.description || ''}
        bugArea={selectedDescription?.area || ''}
      />
    </div>
  );
};

export default BugReports;